"use client";

import { useState, useRef, useEffect, DragEvent, ChangeEvent } from "react";
import {
  X,
  Upload,
  Sparkles,
  FileText,
  Building2,
  Loader2,
  AlertCircle,
  CheckCircle,
  HelpCircle,
} from "lucide-react";
import {
  listAdminOrganizationsApiV1AdminOrganizationsGet,
  generateInteractiveCourseFromDocApiV1AdminInteractiveCoursesGenerateFromDocPost,
} from "@/src/api/sdk.gen";
import { formatApiErrorMessage } from "@/src/lib/error-utils";

interface GenerateFromDocModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newCourseId: number) => void;
}

type Org = { id: number; name: string };

const ALLOWED_EXTENSIONS = [".docx", ".pdf", ".md", ".txt"];

export function GenerateFromDocModal({
  isOpen,
  onClose,
  onSuccess,
}: GenerateFromDocModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [titleOverride, setTitleOverride] = useState<string>("");
  const [customInstructions, setCustomInstructions] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [progressStage, setProgressStage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    listAdminOrganizationsApiV1AdminOrganizationsGet()
      .then(({ data, error }) => {
        if (!error && data) {
          const list = data as unknown as Org[];
          setOrgs(list);
          if (list.length > 0 && !selectedOrgId) {
            setSelectedOrgId(String(list[0].id));
          }
        }
      })
      .catch(() => {});
  }, [isOpen]);

  const reset = () => {
    setFile(null);
    setTitleOverride("");
    setCustomInstructions("");
    setIsGenerating(false);
    setProgressStage("");
    setError(null);
    setIsDragging(false);
  };

  const handleClose = () => {
    if (isGenerating) return;
    reset();
    onClose();
  };

  const handleFile = (selectedFile: File) => {
    const ext = "." + selectedFile.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Invalid file type. Supported formats: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return;
    }
    if (selectedFile.size > 25 * 1024 * 1024) {
      setError("File size exceeds the 25MB limit.");
      return;
    }
    setFile(selectedFile);
    setError(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  const handleGenerate = async () => {
    if (!file) {
      setError("Please select or drop a document file.");
      return;
    }
    if (!selectedOrgId) {
      setError("Please select an organization.");
      return;
    }

    setIsGenerating(true);
    setError(null);
    setProgressStage("Reading and parsing document contents...");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("organization_id", selectedOrgId);
    if (titleOverride.trim()) {
      formData.append("title_override", titleOverride.trim());
    }
    if (customInstructions.trim()) {
      formData.append("custom_instructions", customInstructions.trim());
    }

    try {
      setTimeout(() => {
        setProgressStage("Synthesizing multi-level curriculum with OpenAI (levels, modules, lessons)...");
      }, 3000);
      setTimeout(() => {
        setProgressStage("Structuring interactive blocks (drag-and-drop, quizzes, sequences, cards)...");
      }, 8000);

      const { data, error: reqError } =
        await generateInteractiveCourseFromDocApiV1AdminInteractiveCoursesGenerateFromDocPost({
          body: {
            file,
            organization_id: Number(selectedOrgId),
            title_override: titleOverride.trim() || undefined,
            custom_instructions: customInstructions.trim() || undefined,
          },
        });

      if (reqError || !data) {
        throw new Error(formatApiErrorMessage(reqError, "Course generation failed."));
      }

      const createdId = (data as any)?.id;
      if (onSuccess && createdId) {
        onSuccess(createdId);
      }
      handleClose();
    } catch (err) {
      setError(formatApiErrorMessage(err, "Failed to generate course from document."));
      setIsGenerating(false);
      setProgressStage("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
        />

        <div className="relative w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-900/5">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-500 text-white shadow-md shadow-indigo-500/20">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  AI Course Generation from Document
                </h2>
                <p className="text-xs text-slate-500">
                  Upload a Word (.docx), PDF, Markdown, or Text file to generate an interactive course.
                </p>
              </div>
            </div>
            {!isGenerating && (
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-xs font-semibold text-rose-700">
              <AlertCircle className="h-4 w-4 shrink-0 text-rose-500" />
              <span>{error}</span>
            </div>
          )}

          {isGenerating ? (
            /* Loading State */
            <div className="my-8 flex flex-col items-center justify-center space-y-4 text-center">
              <div className="relative">
                <div className="h-16 w-16 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                <Sparkles className="absolute inset-0 m-auto h-6 w-6 text-indigo-600 animate-pulse" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-bold text-slate-800">Generating Course Architecture...</p>
                <p className="text-xs text-slate-500 max-w-sm">{progressStage}</p>
              </div>
              <p className="text-[11px] text-slate-400">
                This takes 10-30 seconds depending on document length. Please do not close this window.
              </p>
            </div>
          ) : (
            /* Form Fields */
            <div className="space-y-4">
              {/* Dropzone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`group relative flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-all ${
                  isDragging
                    ? "border-indigo-500 bg-indigo-50/50 scale-[1.01]"
                    : file
                    ? "border-emerald-400 bg-emerald-50/30"
                    : "border-slate-300 hover:border-indigo-400 hover:bg-slate-50/60"
                }`}
              >
                <div
                  className={`mb-3 flex h-12 w-12 items-center justify-center rounded-full transition-transform group-hover:scale-110 ${
                    file ? "bg-emerald-100 text-emerald-600" : "bg-indigo-50 text-indigo-600"
                  }`}
                >
                  {file ? <FileText className="h-6 w-6" /> : <Upload className="h-6 w-6" />}
                </div>

                {file ? (
                  <div className="space-y-0.5">
                    <p className="max-w-xs truncate text-sm font-bold text-slate-800">
                      {file.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB • Ready to synthesize
                    </p>
                    <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                      <CheckCircle className="h-3.5 w-3.5" /> Click or drop another file to replace
                    </span>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      Click to browse or drop your document here
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      Supports <strong className="font-semibold text-slate-600">.docx, .pdf, .md, .txt</strong> (Max 25MB)
                    </p>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.pdf,.md,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                  className="hidden"
                />
              </div>

              {/* Organization Selector */}
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  Target Organization <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <select
                    value={selectedOrgId}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm font-medium text-slate-800 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  >
                    <option value="">Select an organization...</option>
                    {orgs.map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Course Title Override (Optional) */}
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  Course Title <span className="font-normal normal-case text-slate-400">(Optional — auto-detected from document if empty)</span>
                </label>
                <input
                  type="text"
                  value={titleOverride}
                  onChange={(e) => setTitleOverride(e.target.value)}
                  placeholder="e.g. Advanced Machine Learning Engineering"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              {/* Custom Prompt Instructions (Optional) */}
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  Authoring Instructions <span className="font-normal normal-case text-slate-400">(Optional)</span>
                </label>
                <textarea
                  rows={2}
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="e.g. Emphasize interactive sequencing and drag-and-drop tasks in every module..."
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={!file || !selectedOrgId}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-500/20 transition-all hover:from-indigo-700 hover:to-violet-700 hover:shadow-lg hover:shadow-indigo-500/30 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  Generate Course
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
